import ImportExcelToBlueticks from '@common-components/tasks-panel/import-tasks-from-excel/import-excel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { bulkActions } from '@common-reducers/BulkActionsReducer';
import { setIsImportModalOpen } from '@common-reducers/CampaignReducer';
import { handleActiveBulkViewNavigationThunk } from '@common-reducers/CampaignThunks';
import { importItemsActions } from '@common-reducers/ImportItemsReducer';
import { modalActions } from '@common-reducers/ModalReducer';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';



import { CheckCircle } from 'lucide-react';

export default function ImportCSVOrGoogleSheets() {
    const dispatch = useAppDispatch()
    const { t } = useTranslation(namespace)

    const isImportModalOpen = useAppSelector(state => state.CampaignReducer.isImportModalOpen)

    const handleExitNewAudienceView = () => {
        dispatch(handleActiveBulkViewNavigationThunk({ navigate: "fromNewAudience" }))

    }

    const importedAudienceId = useAppSelector(state => state.ImportItemsReducer.importedAudienceId);

    const handleFinishImporting = (boardId?: string) => {
        if (boardId) {
            dispatch(importItemsActions.setImportedAudienceId(boardId));
        } else {
            dispatch(setIsImportModalOpen(false))
            handleExitNewAudienceView()
        }
    }

    const handleClose = () => {
        dispatch(setIsImportModalOpen(false))
        dispatch(importItemsActions.setStep(0))
        dispatch(importItemsActions.resetImport())
        dispatch(modalActions.setImportType(''))
        dispatch(modalActions.setComponentToRender(''))
        dispatch(bulkActions.setBulkTasksMap({}))
        dispatch(modalActions.setOpenedby(''))
        dispatch(importItemsActions.setNewAudienceName(null))
        dispatch(importItemsActions.setImportedAudienceId(null))
        handleExitNewAudienceView()
    }


    return (
        <Dialog open={isImportModalOpen} onOpenChange={handleClose}>
            <DialogContent 
                className="max-w-[1024px] w-full h-[90vh] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0 rounded-xl"
                style={{
                    backgroundColor: 'var(--color-surface)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                    height: '90vh',
                    maxHeight: '90vh'
                }}
            >
                {/* Hide the default close button from DialogContent */}
                <style>{`
                    [data-radix-dialog-content] button[class*="absolute right-4 top-4"] {
                        display: none !important;
                    }
                `}</style>
                
                {/* Custom close button - modern and crisp */}
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        // `end-4`, not `right-4`: this dialog's close button sat on the wrong
                        // side in Hebrew/RTL.
                        "absolute top-4 end-4 z-[2000]",
                        "w-8 h-8 rounded-lg",
                        "bg-white/80 dark:bg-slate-800/80",
                        "backdrop-blur-sm",
                        "text-slate-600 dark:text-slate-300",
                        "border border-slate-200 dark:border-slate-700",
                        "shadow-sm",
                        "hover:bg-white dark:hover:bg-slate-700",
                        "hover:border-slate-300 dark:hover:border-slate-600",
                        "transition-all duration-200",
                        "hover:scale-105 active:scale-95"
                    )}
                    onClick={handleClose}
                    aria-label={t('dialog.close')}
                >
                    <X className="h-4 w-4" />
                </Button>

                {/* Dialog Header with Title and Description for accessibility - hidden visually but accessible */}
                <DialogHeader className="sr-only">
                    <DialogTitle>{t('audiences.importTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('audiences.importDescription')}
                    </DialogDescription>
                </DialogHeader>

                {/* Main content - fills entire dialog */}
                <div className="flex-1 min-h-0 flex flex-col">
                    {importedAudienceId ? (
                        <div className="flex flex-col items-center justify-center h-full gap-6 animate-in fade-in zoom-in duration-300">
                           <div className="rounded-full bg-green-100 p-8 dark:bg-green-900/30">
                                <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-400" />
                           </div>
                           <div className="text-center space-y-2">
                               <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                                    {t('audiences.importSuccess')}
                               </h2>
                               <p className="text-slate-500 dark:text-slate-400">
                                    {t('audiences.importDoneDesc', { defaultValue: 'Your contacts have been imported and are ready to use.' })}
                               </p>
                           </div>
                           <div className="flex items-center gap-3 mt-4">
                                <Button
                                    onClick={handleClose}
                                    size="lg"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all"
                                >
                                    {t('common.done', { defaultValue: 'Done' })}
                                </Button>
                           </div>
                        </div>
                    ) : (
                        <ImportExcelToBlueticks handleFinishImporting={handleFinishImporting} />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}